import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Terminal,
  Image as ImageIcon,
  X,
  Loader2,
  Eye,
} from "lucide-react";
import { useStarterPlan } from "../contexts/OrganizationPlanContext";

export default function TestExecutor({ darkMode }) {
  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";
  const headerBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";

  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const projectId = selectedProject?.project_id;
  const canExecuteTests =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");

  const [targetUrl, setTargetUrl] = useState("https://");
  const [testPrompt, setTestPrompt] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const [isHeadlessMode, setIsHeadlessMode] = useState(true);
  const [executionPhase, setExecutionPhase] = useState("idle");
  const [terminalLogs, setTerminalLogs] = useState([]);
  const [liveFrame, setLiveFrame] = useState(Date.now());

  const [selectedResult, setSelectedResult] = useState(null);
  const [activeTab, setActiveTab] = useState("terminal");
  const [recentExecutions, setRecentExecutions] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    let interval;
    if (isExecuting && !isHeadlessMode && executionPhase === "executing") {
      interval = setInterval(() => {
        setLiveFrame(Date.now());
      }, 300);
    }
    return () => clearInterval(interval);
  }, [isExecuting, isHeadlessMode, executionPhase]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!projectId) return;
      try {
        const response = await axios.get(
          `https://api.testoraglobal.in/api/history/${projectId}`,
        );
        if (Array.isArray(response.data)) {
          const formattedHistory = response.data.map((run) => ({
            id: run._id,
            title: run.title,
            status: run.status,
            time: run.time || "Recently",
            duration: run.duration,
            log: run.log,
            headless: run.headless !== undefined ? run.headless : true,
            screenshots: run.screenshots
              ? run.screenshots.map((s) => `https://api.testoraglobal.in${s}`)
              : run.screenshot
                ? [`https://api.testoraglobal.in${run.screenshot}`]
                : [],
          }));
          setRecentExecutions(formattedHistory);
        } else {
          setRecentExecutions([]);
        }
      } catch (error) {
        console.error("Failed to fetch execution history", error);
        setRecentExecutions([]);
      }
    };
    fetchHistory();
  }, [projectId]);

  const triggerOptionsModal = () => {
    if (!canExecuteTests) return;
    if (
      !targetUrl ||
      targetUrl === "https://" ||
      targetUrl === "url: https://www.example.com"
    )
      return alert("Please provide a Target URL.");
    if (!testPrompt.trim()) return alert("Please describe the test scenario.");
    if (!projectId)
      return alert("Please select a project before running tests.");
    setShowOptionsModal(true);
  };

  const confirmExecution = async (isHeadless) => {
    if (!canExecuteTests) return;
    setShowOptionsModal(false);
    setIsHeadlessMode(isHeadless);
    setIsExecuting(true);
    setExecutionPhase("generating");

    setTerminalLogs([
      `> Initializing Autonomous Agent...`,
      `> Target URL configured: ${targetUrl}`,
      `> Analyzing test scenario and DOM structure...`,
      `> Synthesizing automation parameters...`,
    ]);

    const phaseTimer = setTimeout(() => {
      setExecutionPhase("executing");
      setTerminalLogs((prev) => [
        ...prev,
        `> Automation script compiled successfully.`,
        `> Establishing secure browser connection...`,
        `> Executing test instructions...`,
      ]);
    }, 6000);

    const startTime = Date.now();

    try {
      const payload = {
        test_case: {
          "test-case-title": "Ad-Hoc AI Execution",
          test_steps: [testPrompt],
          expected_result: "Execute the steps successfully without errors.",
        },
        target_url: targetUrl,
        project_id: projectId,
        headless: isHeadless,
      };

      const response = await axios.post(
        "https://api.testoraglobal.in/api/run-autonomous-test",
        payload,
      );
      const data = response.data;
      const duration = ((Date.now() - startTime) / 1000).toFixed(1) + "s";

      const newRun = {
        id: Date.now(),
        title:
          testPrompt.length > 40
            ? testPrompt.substring(0, 40) + "..."
            : testPrompt,
        status: data.status,
        time: "Just now",
        duration: duration,
        log: data.console_output || data.message || "No logs returned.",
        screenshots: data.screenshots
          ? data.screenshots.map((s) => `https://api.testoraglobal.in${s}`)
          : [],
        headless: isHeadless,
      };

      setRecentExecutions((prev) => [
        newRun,
        ...(Array.isArray(prev) ? prev : []),
      ]);
      setActiveTab("terminal");
      setSelectedResult(newRun);
      setTestPrompt("");
    } catch (error) {
      const backendError = error.response?.data?.message || error.message;
      alert(`Execution failed: ${backendError}`);
    } finally {
      clearTimeout(phaseTimer);
      setIsExecuting(false);
      setExecutionPhase("idle");
    }
  };

  return (
    <div className="px-8 pb-12 max-w-5xl mx-auto animate-in fade-in duration-500 relative">
      {/* HEADER */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">
          Autonomous Test Executor
        </h2>
        <p className="text-gray-500 text-sm mt-1">
          Describe your test in natural language and let AI execute it
        </p>
      </div>

      {/* INPUT CARD */}
      <div className={`p-6 rounded-2xl border mb-8 ${cardBg} ${borderColor}`}>
        <h3 className="font-bold text-sm mb-4">Natural Language Test Input</h3>

        <div className="mb-4 relative">
          <input
            type="text"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            disabled={isExecuting}
            className={`w-full px-4 py-3 rounded-xl border text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
              darkMode
                ? "bg-[#0f111a] border-gray-800 text-white"
                : "bg-gray-50 border-gray-200 text-gray-900"
            }`}
            placeholder="url: https://www.example.com"
          />
        </div>

        <div className="relative">
          <textarea
            value={testPrompt}
            onChange={(e) => setTestPrompt(e.target.value)}
            disabled={isExecuting}
            className={`w-full p-4 rounded-xl border text-sm min-h-[160px] resize-none focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
              darkMode
                ? "bg-[#0f111a] border-gray-800 text-gray-300"
                : "bg-gray-50 border-gray-200 text-gray-700"
            }`}
            placeholder="Example: Test the login flow by going to the login page..."
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Describe the test scenario in plain English.
          </p>
          {canExecuteTests && (
            <button
              onClick={triggerOptionsModal}
              disabled={isExecuting || !testPrompt.trim()}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg ${
                isExecuting
                  ? "bg-purple-500/50 text-white cursor-wait"
                  : "bg-purple-600 hover:bg-purple-500 text-white hover:shadow-purple-500/25"
              }`}
            >
              {isExecuting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Play size={16} fill="currentColor" />
              )}
              {isExecuting ? "Executing AI Agent..." : "Execute Test"}
            </button>
          )}
        </div>
      </div>

      {/* LIVE EXECUTION DASHBOARD */}
      {isExecuting && (
        <div className="flex flex-row w-full gap-6 mb-8 h-[450px] animate-in fade-in slide-in-from-top-4 duration-500">
          {/* Terminal Logs */}
          <div className="w-1/2 bg-[#0a0a0a] rounded-xl border border-gray-800 p-5 font-mono text-sm overflow-hidden flex flex-col shadow-2xl">
            <div className="text-gray-500 flex items-center gap-2 border-b border-gray-800 pb-3 mb-3 uppercase tracking-wider text-xs font-bold">
              <Terminal size={14} /> Agent Console
            </div>
            <div className="flex-1 overflow-y-auto space-y-1.5 text-green-400">
              {terminalLogs.map((log, index) => (
                <div key={index} className="opacity-90">
                  {log}
                </div>
              ))}
              <div className="text-gray-500 animate-pulse">_</div>
            </div>
          </div>

          {/* Live Video / Headless Status */}
          {/* Live Video / Headless Status */}
          <div className="w-1/2 bg-black rounded-xl border border-gray-800 overflow-hidden relative shadow-2xl">
            {/* Fixed-size stage — everything renders inside this same box, no layout shift */}
            <div className="w-full h-full flex items-center justify-center relative">
              {isHeadlessMode ? (
                <div className="text-gray-500 text-center px-4 flex flex-col items-center">
                  <Terminal size={32} className="mb-3 opacity-20" />
                  <p className="font-bold text-gray-400">
                    Background Mode Active
                  </p>
                  <p className="text-xs mt-2 leading-relaxed text-gray-600">
                    Execution is running invisibly to save memory.
                    <br />
                    Live visual stream is disabled.
                  </p>
                </div>
              ) : (
                <>
                  {/* Live frame — always mounted once we're in "executing" phase, fades in on load */}
                  {executionPhase === "executing" && (
                    <img
                      src={`https://api.testoraglobal.in/uploads/screenshots/live_frame.png?t=${liveFrame}`}
                      alt="Live Stream"
                      className="max-h-full max-w-full object-contain transition-opacity duration-300"
                      style={{ opacity: liveFrame ? 1 : 0 }}
                      onError={(e) => (e.target.style.opacity = 0)}
                      onLoad={(e) => (e.target.style.opacity = 1)}
                    />
                  )}

                  {/* Waiting overlay — shown during "generating" and hidden once frames start arriving */}
                  {executionPhase === "generating" && (
                    <div className="flex flex-col items-center justify-center text-center px-6">
                      <div className="relative mb-5">
                        <div className="w-14 h-14 rounded-full border-2 border-blue-500/20 flex items-center justify-center">
                          <Loader2
                            size={24}
                            className="animate-spin text-blue-500"
                          />
                        </div>
                      </div>
                      <p className="font-semibold text-gray-300 text-sm">
                        Preparing live browser session...
                      </p>
                      <p className="text-xs mt-1.5 text-gray-600 leading-relaxed max-w-[220px]">
                        The video feed will appear here automatically once the
                        agent starts interacting with the page.
                      </p>
                    </div>
                  )}

                  {/* Live badge — only once the stream is actually flowing */}
                  {executionPhase === "executing" && (
                    <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full border border-gray-700">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-[10px] font-bold text-white tracking-widest uppercase">
                        Live
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RECENT EXECUTIONS */}
      <div>
        <h3 className="font-bold text-sm mb-4">Recent Executions</h3>
        <div
          className={`rounded-2xl border overflow-hidden ${cardBg} ${borderColor}`}
        >
          <div className="divide-y divide-gray-800/50 dark:divide-gray-800 max-h-[400px] overflow-y-auto custom-scrollbar">
            {!Array.isArray(recentExecutions) ||
            recentExecutions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                No test executions found for this project yet.
              </div>
            ) : (
              recentExecutions.map((run) => (
                <div
                  key={run.id}
                  onClick={() => {
                    setActiveTab("terminal");
                    setSelectedResult(run);
                  }}
                  className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                    darkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {run.status === "Pass" ? (
                      <CheckCircle size={20} className="text-green-500" />
                    ) : (
                      <XCircle size={20} className="text-red-500" />
                    )}
                    <div>
                      <p
                        className={`text-sm font-semibold ${darkMode ? "text-gray-200" : "text-gray-800"}`}
                      >
                        {run.title}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {run.time}
                        </span>
                        <span>•</span>
                        <span>{run.duration}</span>
                        <span>•</span>
                        <span>
                          {run.headless ? "Background Run" : "Live Execution"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-400 hover:text-purple-500 transition-colors">
                    <Terminal size={18} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* OPTIONS MODAL */}
      {showOptionsModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div
            className={`w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
              darkMode
                ? "bg-[#161922] border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            <h3 className="text-xl font-bold mb-2">Execution Mode</h3>
            <p className="text-sm text-gray-500 mb-6">
              How would you like to run this automated test?
            </p>

            <div className="space-y-4 mb-6">
              <button
                onClick={() => confirmExecution(true)}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  darkMode
                    ? "border-gray-700 hover:border-purple-500 hover:bg-purple-500/10"
                    : "border-gray-200 hover:border-purple-500 hover:bg-purple-50"
                }`}
              >
                <div className="flex items-center gap-3 mb-1">
                  <ImageIcon size={18} className="text-purple-500" />
                  <span className="font-bold">Background Run (Headless)</span>
                </div>
                <p className="text-xs text-gray-500 pl-7">
                  Runs invisibly. Generates step-by-step visual artifacts for
                  detailed trace debugging.
                </p>
              </button>

              <button
                onClick={() => confirmExecution(false)}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  darkMode
                    ? "border-gray-700 hover:border-blue-500 hover:bg-blue-500/10"
                    : "border-gray-200 hover:border-blue-500 hover:bg-blue-50"
                }`}
              >
                <div className="flex items-center gap-3 mb-1">
                  <Play size={18} className="text-blue-500" />
                  <span className="font-bold">Watch Live (Embedded)</span>
                </div>
                <p className="text-xs text-gray-500 pl-7">
                  Streams a live view of the AI interacting with the browser.
                  Artifacts will also be saved.
                </p>
              </button>
            </div>

            <button
              onClick={() => setShowOptionsModal(false)}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* RESULT MODAL */}
      {selectedResult && !isExecuting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div
            className={`w-full max-w-4xl rounded-2xl shadow-2xl border flex flex-col overflow-hidden ${
              darkMode
                ? "bg-[#161922] border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Modal Header */}
            <div
              className={`px-6 py-4 border-b flex items-center justify-between ${headerBg} ${borderColor}`}
            >
              <div className="flex items-center gap-3">
                {selectedResult.status === "Pass" ? (
                  <CheckCircle size={24} className="text-green-500" />
                ) : (
                  <XCircle size={24} className="text-red-500" />
                )}
                <div>
                  <h3 className="font-bold text-lg leading-none">
                    {selectedResult.status === "Pass"
                      ? "Execution Passed"
                      : "Execution Failed"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedResult.duration} execution time
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedResult(null)}
                className="p-2 hover:bg-gray-500/20 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Tabs */}
            <div
              className={`px-6 py-2 border-b flex gap-4 ${borderColor} bg-gray-900/10`}
            >
              <button
                onClick={() => setActiveTab("terminal")}
                className={`flex items-center gap-2 py-2 text-sm font-bold border-b-2 transition-colors ${
                  activeTab === "terminal"
                    ? "border-purple-500 text-purple-500"
                    : "border-transparent text-gray-500 hover:text-gray-300"
                }`}
              >
                <Terminal size={16} /> Console Logs
              </button>
              <button
                onClick={() => setActiveTab("screenshot")}
                className={`flex items-center gap-2 py-2 text-sm font-bold border-b-2 transition-colors ${
                  activeTab === "screenshot"
                    ? "border-purple-500 text-purple-500"
                    : "border-transparent text-gray-500 hover:text-gray-300"
                }`}
              >
                <ImageIcon size={16} /> Visual Artifacts
              </button>
            </div>

            {/* Tab Content */}
            <div className="h-[500px] bg-black p-0 overflow-hidden relative">
              {activeTab === "terminal" ? (
                <div className="p-6 h-full overflow-y-auto font-mono text-sm">
                  <pre
                    className={`${
                      selectedResult.status === "Pass"
                        ? "text-green-400"
                        : "text-red-400"
                    } whitespace-pre-wrap leading-relaxed`}
                  >
                    {selectedResult.log}
                  </pre>
                </div>
              ) : (
                <div className="h-full overflow-y-auto bg-gray-900 p-6">
                  {selectedResult.screenshots &&
                  selectedResult.screenshots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-6">
                      {selectedResult.screenshots.map((imgUrl, idx) => (
                        <div key={idx} className="flex flex-col gap-2">
                          <span className="text-xs font-bold text-gray-400 bg-gray-800 px-3 py-1 rounded-full w-max">
                            Step {idx + 1}
                          </span>
                          <div className="relative group rounded border border-gray-700 bg-black overflow-hidden">
                            <img
                              src={imgUrl}
                              alt={`Step ${idx + 1} Artifact`}
                              className="w-full h-36 object-cover"
                            />
                            <button
                              onClick={() => setLightboxImg(imgUrl)}
                              className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Eye size={28} className="text-white" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center p-4">
                      <p className="text-gray-500 flex items-center gap-2">
                        <ImageIcon size={20} /> No screenshot artifacts
                        available
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-6 animate-in fade-in"
          onClick={() => setLightboxImg(null)}
        >
          <button
            onClick={() => setLightboxImg(null)}
            className="absolute top-5 right-5 p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <X size={22} className="text-white" />
          </button>
          <img
            src={lightboxImg}
            alt="Full view"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
